import { useState, useEffect } from 'react'
import { Plus, Trash2, LogOut, Youtube, AlertCircle } from 'lucide-react'

interface Channel {
  id: string
  title: string
  thumbnail: string
  subscriber_count?: string
  created_at?: string
}

interface ChannelManagerProps {
  onLogout: () => void
}

export function ChannelManager({ onLogout }: ChannelManagerProps) {
  const [channels, setChannels] = useState<Channel[]>([])
  const [newChannelId, setNewChannelId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const fetchChannels = async () => {
    try {
      const response = await fetch('/.netlify/functions/get-channels')
      if (response.ok) {
        const data = await response.json()
        setChannels(data.channels || [])
      }
    } catch (error) {
      console.error('Failed to fetch channels:', error)
    }
  }

  useEffect(() => {
    fetchChannels()
  }, [])

  const extractChannelId = (input: string): string => {
    const trimmed = input.trim()
    
    // If it's already just a channel ID
    if (trimmed.match(/^[a-zA-Z0-9_-]{24}$/)) {
      return trimmed
    }
    
    // Extract from various YouTube URL formats
    const patterns = [
      /youtube\.com\/channel\/([a-zA-Z0-9_-]{24})/,
      /youtube\.com\/c\/([a-zA-Z0-9_-]+)/,
      /youtube\.com\/user\/([a-zA-Z0-9_-]+)/,
      /youtube\.com\/@([a-zA-Z0-9_.-]+)/,
    ]
    
    for (const pattern of patterns) {
      const match = trimmed.match(pattern)
      if (match) {
        return match[1]
      }
    }
    
    return trimmed
  }

  const addChannel = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newChannelId.trim()) return

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const channelId = extractChannelId(newChannelId)
      const response = await fetch('/.netlify/functions/add-channel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ channelId }),
      })

      const data = await response.json()

      if (response.ok) {
        setChannels(data.channels)
        setNewChannelId('')
        setSuccess('Channel added successfully!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(data.error || 'Failed to add channel')
      }
    } catch (error) {
      setError('An error occurred while adding the channel')
    } finally {
      setLoading(false)
    }
  }

  const removeChannel = async (channelId: string) => {
    if (!confirm('Are you sure you want to remove this channel?')) return

    try {
      const response = await fetch('/.netlify/functions/remove-channel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ channelId }),
      })

      if (response.ok) {
        const data = await response.json()
        setChannels(data.channels)
        setSuccess('Channel removed successfully!')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to remove channel')
      }
    } catch (error) {
      setError('An error occurred while removing the channel')
    }
  }

  const handleLogout = async () => {
    try {
      await fetch('/.netlify/functions/admin-logout', { method: 'POST' })
    } finally {
      onLogout()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Channel Manager</h1>
              <p className="text-gray-600 mt-1">Manage the whitelist of safe channels for kids</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Add Channel Form */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Add New Channel</h2>
          
          <form onSubmit={addChannel} className="space-y-4">
            <div>
              <label htmlFor="channelId" className="block text-sm font-medium text-gray-700 mb-2">
                Channel URL or ID
              </label>
              <input
                type="text"
                id="channelId"
                value={newChannelId}
                onChange={(e) => setNewChannelId(e.target.value)}
                placeholder="https://www.youtube.com/@channelname or channel ID"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Supports: Channel URLs, @username, /c/channelname, /user/username, or direct channel ID
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>{loading ? 'Adding...' : 'Add Channel'}</span>
            </button>
          </form>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg">
              {success}
            </div>
          )}
        </div>

        {/* Channels List */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              Whitelisted Channels ({channels.length})
            </h2>
          </div>

          {channels.length === 0 ? (
            <div className="text-center py-12">
              <Youtube className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No channels added yet</h3>
              <p className="text-gray-500">Add some safe channels for kids to watch!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {channels.map((channel) => (
                <div
                  key={channel.id}
                  className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <img
                    src={channel.thumbnail}
                    alt={channel.title}
                    className="w-12 h-12 rounded-full"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 truncate">{channel.title}</h3>
                    <p className="text-sm text-gray-500 truncate">ID: {channel.id}</p>
                    {channel.subscriber_count && (
                      <p className="text-xs text-gray-400">{channel.subscriber_count} subscribers</p>
                    )}
                  </div>
                  <button
                    onClick={() => removeChannel(channel.id)}
                    className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}